import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpen,
  Calculator,
  FileText,
  FolderOpen,
  Hammer,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  StepMock,
  type LifecycleMockCopy,
  type LifecycleMockStory,
} from "@/components/JobLifecycleMocks";
import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/utils/analytics";
import {
  getLocalizedPath,
  getRouteByKey,
  isRouteAvailable,
  type SiteLanguage,
} from "@/utils/routing";

export const JOB_LIFECYCLE_STEP_SLUGS = [
  "cenove-ponuky",
  "projektove-riadenie",
  "vykaz-prace-a-evidencia-dochadzky",
  "stavebny-dennik",
  "online-fakturacia",
  "uctovnictvo",
] as const;

export const JOB_LIFECYCLE_CHIP_SLUGS = [
  "cenove-ponuky",
  "projektove-riadenie",
  "vykaz-prace-a-evidencia-dochadzky",
  "stavebny-dennik",
  "bezpecnost-kvalita",
  "online-fakturacia",
  "uctovnictvo",
] as const;

export const JOB_LIFECYCLE_PROFESSION_IDS = [
  "bathroom",
  "electrical",
  "roof",
  "painting",
  "mason",
  "carpenter",
  "plumber",
  "tiler",
  "heating",
  "drywall",
  "flooring",
  "windows",
] as const;

type Audience = "all" | "crew";
export type LifecycleMode = "solo" | "crew" | "developer";
const LIFECYCLE_MODES = ["solo", "crew", "developer"] as const;

const showsCrewSteps = (mode: LifecycleMode) => mode === "crew" || mode === "developer";

const STEP_META: Array<{
  slug: (typeof JOB_LIFECYCLE_STEP_SLUGS)[number];
  audience: Audience;
  chips: readonly string[];
}> = [
  { slug: "cenove-ponuky", audience: "all", chips: ["cenove-ponuky"] },
  { slug: "projektove-riadenie", audience: "all", chips: ["projektove-riadenie"] },
  {
    slug: "vykaz-prace-a-evidencia-dochadzky",
    audience: "all",
    chips: ["vykaz-prace-a-evidencia-dochadzky"],
  },
  {
    slug: "stavebny-dennik",
    audience: "crew",
    chips: ["stavebny-dennik", "bezpecnost-kvalita"],
  },
  { slug: "online-fakturacia", audience: "all", chips: ["online-fakturacia"] },
  { slug: "uctovnictvo", audience: "all", chips: ["uctovnictvo"] },
];

const STEP_ICONS = [FileText, FolderOpen, Hammer, ShieldCheck, Receipt, Calculator];

interface LifecycleStep {
  title: string;
  summary: string;
  youDo: string;
  systemDoes: string;
  benefit: string;
  moduleLabel: string;
  today: string;
  bidmio: string;
}

interface LifecycleProfession {
  id: string;
  label: string;
  title: string;
  client: string;
  amount: string;
  jobNumber: string;
  hourLine: string;
  materialLine: string;
  photoLine: string;
  diaryLine: string;
  progress: string[];
}

const moduleHref = (canonicalSlug: string, lang: SiteLanguage) =>
  getLocalizedPath(`/moduly/${canonicalSlug}/`, lang);

const visibleIndexes = (mode: LifecycleMode) =>
  STEP_META.map((step, index) => ({ step, index })).filter(
    ({ step }) => showsCrewSteps(mode) || step.audience === "all"
  );

interface StepDetailProps {
  step: LifecycleStep;
  originalIndex: number;
  displayNumber: number;
  mock: LifecycleMockCopy;
  story: LifecycleMockStory;
  lang: SiteLanguage;
}

const StepDetail = ({
  step,
  originalIndex,
  displayNumber,
  mock,
  story,
  lang,
}: StepDetailProps) => {
  const { t } = useTranslation();
  const slug = STEP_META[originalIndex].slug;

  return (
    <div className="space-y-5 md:space-y-7 min-w-0">
      <div className="hidden md:block">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
          {t("pages.home.jobLifecycle.stepLabel", { number: displayNumber })}
        </p>
        <h3 className="text-2xl sm:text-3xl font-bold text-foreground break-words leading-tight">
          {step.title}
        </h3>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-snug max-w-2xl">
          {step.summary}
        </p>
      </div>

      <StepMock stepIndex={originalIndex} copy={mock} story={story} />

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg border border-destructive/10 bg-destructive/[0.03] px-2.5 py-2 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-destructive/80 mb-0.5">
            {t("pages.home.jobLifecycle.todayLabel")}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">{step.today}</p>
        </div>
        <div className="rounded-lg border border-success/10 bg-success/[0.04] px-2.5 py-2 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-success/80 mb-0.5">
            {t("pages.home.jobLifecycle.bidmioLabel")}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">{step.bidmio}</p>
        </div>
        <div className="rounded-lg border border-border/40 bg-muted/20 px-2.5 py-2 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
            {originalIndex === 2
              ? t("pages.home.jobLifecycle.crewOnSite")
              : t("pages.home.jobLifecycle.youLabel")}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">{step.youDo}</p>
        </div>
        <div className="rounded-lg border border-border/40 bg-muted/20 px-2.5 py-2 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
            {t("pages.home.jobLifecycle.systemLabel")}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">{step.systemDoes}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground leading-snug">{step.benefit}</p>

      <Button asChild variant="ghost" className="whitespace-normal h-auto px-0 py-1 text-sm text-muted-foreground hover:text-foreground">
        <Link
          to={moduleHref(slug, lang)}
          onClick={() => trackButtonClick(`job-lifecycle-module-${slug}`, "home-job-lifecycle")}
          data-testid="job-lifecycle-module-link"
        >
          {t("pages.home.jobLifecycle.learnMore", { module: step.moduleLabel })}
          <ArrowRight className="ml-1.5 h-3.5 w-3.5 shrink-0" />
        </Link>
      </Button>
    </div>
  );
};

const JobLifecycle = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "sk") as SiteLanguage;
  const [mode, setMode] = useState<LifecycleMode>("crew");
  const [professionId, setProfessionId] = useState<string>("bathroom");
  const [activeOriginal, setActiveOriginal] = useState(0);
  const stepCardRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const shouldScrollToStep = useRef(false);

  const steps = t("pages.home.jobLifecycle.steps", { returnObjects: true }) as LifecycleStep[];
  const professions = t("pages.home.jobLifecycle.professions", {
    returnObjects: true,
  }) as LifecycleProfession[];
  const chipLabels = t("pages.home.jobLifecycle.moduleLabels", { returnObjects: true }) as Record<
    string,
    string
  >;
  const mock = t("pages.home.jobLifecycle.mock", { returnObjects: true }) as LifecycleMockCopy;

  const safeSteps = Array.isArray(steps) ? steps.slice(0, JOB_LIFECYCLE_STEP_SLUGS.length) : [];
  const visible = visibleIndexes(mode);
  const visibleOriginals = visible.map(({ index }) => index);
  const safeActive = visibleOriginals.includes(activeOriginal)
    ? activeOriginal
    : visibleOriginals[0] ?? 0;
  const activeStep = safeSteps[safeActive];
  const displayNumber = visibleOriginals.indexOf(safeActive) + 1;

  const profession =
    (Array.isArray(professions) && professions.find((item) => item.id === professionId)) ||
    (Array.isArray(professions) ? professions[0] : undefined);

  const mockStory: LifecycleMockStory | undefined = profession
    ? {
        title: profession.title,
        client: profession.client,
        amount: profession.amount,
        jobNumber: profession.jobNumber,
        hourLine: profession.hourLine,
        materialLine: profession.materialLine,
        photoLine: profession.photoLine,
        diaryLine: profession.diaryLine,
      }
    : undefined;

  const chips = useMemo(
    () =>
      JOB_LIFECYCLE_CHIP_SLUGS.filter((slug) => {
        if (showsCrewSteps(mode)) return true;
        return slug !== "stavebny-dennik" && slug !== "bezpecnost-kvalita";
      }).map(
        (slug) => ({
          slug,
          label: chipLabels?.[slug] || slug,
          href: moduleHref(slug, lang),
          active: STEP_META[safeActive].chips.includes(slug),
        })
      ),
    [chipLabels, lang, mode, safeActive]
  );

  if (!activeStep || !profession || !mockStory || !mock?.quote) return null;

  const selectStep = (originalIndex: number) => {
    if (originalIndex !== activeOriginal) {
      shouldScrollToStep.current = true;
    }
    setActiveOriginal(originalIndex);
    trackButtonClick(`job-lifecycle-step-${originalIndex + 1}`, "home-job-lifecycle");
  };

  useEffect(() => {
    if (!shouldScrollToStep.current) return;
    shouldScrollToStep.current = false;
    if (window.matchMedia("(min-width: 768px)").matches) return;
    const node = stepCardRefs.current[safeActive];
    if (!node) return;
    requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [safeActive]);

  const selectMode = (next: LifecycleMode) => {
    setMode(next);
    trackButtonClick(`job-lifecycle-mode-${next}`, "home-job-lifecycle");
    if (next === "solo" && STEP_META[activeOriginal].audience === "crew") {
      setActiveOriginal(2);
    }
  };

  const fillRatio =
    visibleOriginals.length > 1
      ? visibleOriginals.indexOf(safeActive) / (visibleOriginals.length - 1)
      : 0;

  return (
    <section
      id="job-lifecycle"
      className="section-padding px-2 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-accent/10 to-background overflow-x-clip"
      data-testid="section-job-lifecycle"
    >
      <div className="container-custom min-w-0">
        <div className="rounded-xl sm:rounded-3xl border border-border bg-card/60 px-2 py-6 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
        <div className="text-center space-y-3 mb-8 sm:mb-12">
          <Badge
            variant="secondary"
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-primary/10 text-primary whitespace-normal"
          >
            {t("pages.home.jobLifecycle.badge")}
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl text-foreground font-bold leading-tight break-words">
            {t("pages.home.jobLifecycle.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t("pages.home.jobLifecycle.subtitle")}
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 mb-5">
          <div
            className="inline-flex flex-wrap justify-center rounded-full border border-border bg-card p-1"
            role="group"
            aria-label={t("pages.home.jobLifecycle.modeLabel")}
          >
            {LIFECYCLE_MODES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => selectMode(value)}
                className={cn(
                  "px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium whitespace-normal",
                  mode === value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
                data-testid={`job-lifecycle-mode-${value}`}
              >
                {t(
                  `pages.home.jobLifecycle.mode${
                    value === "solo" ? "Solo" : value === "crew" ? "Crew" : "Developer"
                  }`
                )}
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-xl">
            {t(
              mode === "solo"
                ? "pages.home.jobLifecycle.modeSoloHint"
                : mode === "developer"
                  ? "pages.home.jobLifecycle.modeDeveloperHint"
                  : "pages.home.jobLifecycle.modeCrewHint"
            )}
          </p>
          {mode === "solo" && isRouteAvailable("soloOffer", lang) && (
            <Link
              to={getRouteByKey("soloOffer", lang)}
              className="text-sm font-medium text-primary hover:underline"
              data-testid="link-job-lifecycle-solo-offer"
            >
              {t("pages.home.jobLifecycle.soloLandingCta")}
              <ArrowRight className="inline h-4 w-4 ml-1" />
            </Link>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 mb-10">
          {(Array.isArray(professions) ? professions : []).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setProfessionId(item.id);
                trackButtonClick(`job-lifecycle-profession-${item.id}`, "home-job-lifecycle");
              }}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium break-words max-w-full",
                professionId === item.id
                  ? "border-primary/40 bg-primary/5 text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border/60"
              )}
              data-testid={`job-lifecycle-profession-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <ol className="md:hidden space-y-2.5 mb-8" aria-label={t("pages.home.jobLifecycle.title")}>
          {visible.map(({ index: originalIndex }, order) => {
            const step = safeSteps[originalIndex];
            const Icon = STEP_ICONS[originalIndex] || FileText;
            const isActive = originalIndex === safeActive;
            if (!step) return null;
            return (
              <li
                key={step.title}
                ref={(node) => {
                  stepCardRefs.current[originalIndex] = node;
                }}
                className="scroll-mt-32"
              >
                <div
                  className={cn(
                    "rounded-2xl border transition-colors",
                    isActive ? "border-primary/40 bg-background" : "border-border/40 bg-transparent"
                  )}
                >
                  <button
                    type="button"
                    onClick={() => selectStep(originalIndex)}
                    aria-expanded={isActive}
                    className="w-full min-w-0 text-left p-3 touch-manipulation"
                    data-testid={`job-lifecycle-step-mobile-${originalIndex}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted/70 text-muted-foreground"
                        )}
                      >
                        {order + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary shrink-0" />
                          <span className={cn("font-semibold break-words", isActive ? "text-foreground" : "text-muted-foreground")}>{step.title}</span>
                        </span>
                        <span className="mt-1 block text-sm text-muted-foreground leading-relaxed">
                          {step.summary}
                        </span>
                      </span>
                    </div>
                  </button>
                  {isActive && (
                    <div className="px-2.5 pb-3 border-t border-border/50 pt-3">
                      <StepDetail
                        step={step}
                        originalIndex={originalIndex}
                        displayNumber={order + 1}
                        mock={mock}
                        story={mockStory}
                        lang={lang}
                      />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="hidden md:block relative mb-12 overflow-x-clip">
          <span aria-hidden className="absolute left-10 right-10 top-6 h-0.5 bg-border" />
          <span
            aria-hidden
            className="absolute left-10 top-6 h-0.5 bg-primary transition-[width] duration-300"
            style={{ width: `calc((100% - 5rem) * ${fillRatio})` }}
          />
          <ol
            className="relative grid gap-3 lg:gap-4"
            style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}
            aria-label={t("pages.home.jobLifecycle.title")}
          >
            {visible.map(({ index: originalIndex }, order) => {
              const step = safeSteps[originalIndex];
              const Icon = STEP_ICONS[originalIndex] || FileText;
              const isActive = originalIndex === safeActive;
              if (!step) return null;
              return (
                <li key={step.title} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => selectStep(originalIndex)}
                    aria-pressed={isActive}
                    className="relative z-10 w-full min-w-0 text-center group"
                    data-testid={`job-lifecycle-step-${originalIndex}`}
                  >
                    <span
                      className={cn(
                        "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors",
                        isActive
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border/70 bg-background text-muted-foreground group-hover:border-primary/50 group-hover:text-foreground"
                      )}
                    >
                      {order + 1}
                    </span>
                    <span
                      className={cn(
                        "mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg",
                        isActive ? "bg-primary/10 text-primary" : "bg-muted/50 text-muted-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span
                      className={cn(
                        "block text-sm leading-snug break-words",
                        isActive ? "font-semibold text-foreground" : "font-medium text-muted-foreground"
                      )}
                    >
                      {step.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        <Card className="hidden md:block max-w-5xl mx-auto border-border/40 shadow-none overflow-hidden bg-card/40">
          <CardContent className="p-6 sm:p-10 min-w-0">
            <StepDetail
              step={activeStep}
              originalIndex={safeActive}
              displayNumber={displayNumber}
              mock={mock}
              story={mockStory}
              lang={lang}
            />
          </CardContent>
        </Card>

        <div className="mt-12 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
            {t("pages.home.jobLifecycle.modulesHeading")}
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 max-w-4xl mx-auto">
            {chips.map((chip) => (
              <Link
                key={chip.slug}
                to={chip.href}
                onClick={() =>
                  trackButtonClick(`job-lifecycle-chip-${chip.slug}`, "home-job-lifecycle")
                }
                className={cn(
                  "inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-medium break-words transition-colors",
                  chip.active
                    ? "border-primary/40 bg-primary/5 text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border/60"
                )}
                data-testid={`job-lifecycle-chip-${chip.slug}`}
              >
                {chip.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-2">
          <Button asChild variant="ghost" className="whitespace-normal h-auto py-2">
            <Link to={getRouteByKey("modules", lang)} data-testid="job-lifecycle-view-modules">
              {t("pages.home.jobLifecycle.viewModules")}
              <BookOpen className="ml-2 h-4 w-4 shrink-0" />
            </Link>
          </Button>
        </div>
        </div>
      </div>
    </section>
  );
};

export default JobLifecycle;
